<!-- Страница списка транспорта с быстрым переходом к документам -->
<template>
  <div class="container vehicle-container">
    <base-nav title="Транспорт" :show-back="true" back-fallback="Settings">
      <base-button
        icon="license"
        type="button"
        @click="goToVehicleDocuments"
      >
        Документы ТС
      </base-button>
      <base-button
        v-if="vehicles.length > 0"
        @click="exportToExcel"
      >
        Экспорт в Excel
      </base-button>
      <base-primary-button
        icon="addSquare"
        @click="goToVehicleCreate"
      >
        Создать
      </base-primary-button>
    </base-nav>

    <!-- Фильтры -->
    <FilterBlock
      ref="filterBlock"
      :search-fields="searchFields"
      :initial-filters="appliedFilters"
      target-type="Vehicle"
      @filter-change="handleFilterChange"
    />

    <!-- Счетчик результатов -->
    <MetaResultCount
      v-if="!dataIsLoading && vehicles && vehicles.length > 0"
      :pagination-meta="pagination"
    />

    <!-- Переключение вида -->
    <TableToolbar
      v-if="!dataIsLoading && vehicles.length > 0"
      :show-settings="currentView === 'table'"
      :actions="currentView === 'table' ? dropdownItems : []"
      @open-settings="showSettingsModal = true"
    >
      <base-view-toggle v-model="currentView" />
    </TableToolbar>

    <base-loader v-if="dataIsLoading" />

    <!-- Табличный вид -->
    <div
      v-else-if="
        !dataIsLoading &&
        vehicles &&
        vehicles.length > 0 &&
        currentView === 'table'
      "
      class="vehicle-center"
    >
      <TableComponent
        :data="vehicles"
        :columns="tableColumns"
        :ready="!dataIsLoading && tableColumns.length > 0"
        selectable
        @selection-change="onSelectionChange"
        @row-middle-click="
          openRouteInNewTab({
            name: 'VehicleDetails',
            params: { id: $event.id },
          })
        "
        @row-click="goToVehicleDetails"
      >
        <template #cell-registration_number="{ value }">
          <!-- Госномер транспорта в табличном списке -->
          <VehicleRegNumberBadge
            v-if="value"
            :number="value"
            size="sm"
          />
          <span v-else>—</span>
        </template>

        <template #cell-vehicle_documents_summary="{ value }">
          <VehicleDocumentStatusIcons :documents-summary="value" />
        </template>
      </TableComponent>
    </div>

    <!-- Карточный вид -->
    <div
      v-else-if="
        !dataIsLoading &&
        vehicles &&
        vehicles.length > 0 &&
        currentView === 'cards'
      "
      class="vehicle-block"
    >
      <ul class="vehicle-list">
        <li
          class="card vehicle-item"
          v-for="(vehicle, index) in vehicles"
          :key="index"
          @click="goToVehicleDetails(vehicle.id)"
        >
          <div class="item-center">
            <span class="value" v-if="vehicle.model">
              Модель: {{ vehicle.model }}
            </span>

            <span class="value" v-if="vehicle.manufacturer">
              Производитель: {{ vehicle.manufacturer }}
            </span>

            <span class="value" v-if="vehicle.registration_number">
              Гос. номер:
              <!-- Госномер транспорта в карточке списка -->
              <VehicleRegNumberBadge
                :number="vehicle.registration_number"
                size="sm"
              />
            </span>

            <span class="value" v-if="vehicle.odometer">
              Пробег: {{ vehicle.odometer }} км
            </span>
          </div>

          <!-- Информация об интеграции -->
          <div
            class="item-integration-box"
            v-if="vehicle.glonasssoft_vehicle"
          >
            <span class="integration-title">
              Импортировано с ГЛОНАССсофт
            </span>
            {{ vehicle.glonasssoft_integration_login }}
          </div>

          <div
            class="item-bottom"
            v-if="vehicle.created_at || vehicle.updated_at"
          >
            <MetaTime
              :createdAt="vehicle.created_at"
              :updatedAt="vehicle.updated_at"
            />
          </div>
        </li>
      </ul>
    </div>

    <!-- Пагинация -->
    <pagination-component
      v-if="
        pagination.pages > 1 &&
        !dataIsLoading &&
        tableColumns.length > 0
      "
      :page="pagination.page"
      :pages="pagination.pages"
      :series="pagination.series"
      :route-query="currentRouteQuery"
    />

    <div
      class="no-data"
      v-if="!dataIsLoading && vehicles.length == 0"
    >
      Список транспорта пуст
    </div>

    <!-- Модальное окно настроек -->
    <Teleport to="body">
      <TableSettingsModal
        v-if="showSettingsModal"
        resource-model="Vehicle"
        record-type="Vehicle"
        table-name="vehicles"
        @close="showSettingsModal = false"
        @config-saved="handleConfigSaved"
      />
    </Teleport>
  </div>
</template>

<script>
import PaginationComponent from "@/components/PaginationComponent.vue";
import MetaTime from "@/components/Meta/MetaTime.vue";
import FilterBlock from "@/components/Filter/FilterBlock.vue";
import MetaResultCount from "@/components/Meta/MetaResultCount.vue";
import TableComponent from "@/components/TableComponent.vue";
import TableSettingsModal from "@/components/TableSettingsModal.vue";
import TableToolbar from "@/components/Table/TableToolbar.vue";
import VehicleDocumentStatusIcons from "@/components/Vehicle/DocumentStatusIcons.vue";
import VehicleRegNumberBadge from "@/components/Vehicle/VehicleRegNumberBadge.vue";

import vehiclesApi from "@/services/api/vehiclesApi";

import { useNotificationStore } from "@/stores/notificationStore";
import { useTableConfigStore } from "@/stores/tableConfigStore";

import { getCompanyId } from "@/utils/userUtils";
import { exportToExcel } from "@/utils/fileUtils";
import { openRouteInNewTab } from "@/utils/routeUtils";
import {
  filtersToApiParams,
  getInitialFilters,
  getCurrentPageFromUrl,
  updateUrlWithFilters,
  clearUrlFilters,
} from "@/utils/filterUtils";

import rawSearchFields from "@/config/filters/vehicle/vehicles.json";

export default {
  name: "VehiclesPage",
  components: {
    PaginationComponent,
    MetaTime,
    FilterBlock,
    MetaResultCount,
    TableComponent,
    TableSettingsModal,
    TableToolbar,
    VehicleDocumentStatusIcons,
    VehicleRegNumberBadge,
  },
  data() {
    return {
      companyId: getCompanyId(),
      dataIsLoading: true,
      currentView: "table",
      showSettingsModal: false,

      vehicles: [],
      tableColumns: [],
      selectedVehicles: [],

      appliedFilters: {
        searchValues: {},
        showInactive: false,
      },

      dropdownItems: [
        {
          id: "enable",
          label: "Показать",
          action: this.enableSelected,
        },
        {
          id: "disable",
          label: "Скрыть",
          isDanger: true,
          action: this.disableSelected,
        },
      ],

      pagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
        from: 0,
        to: 0,
      },
    };
  },
  watch: {
    "$route.query": {
      immediate: false,
      handler() {
        this.updateFiltersFromUrl();
        this.getVehiclesRequest();
      },
    },

    currentView(newView, oldView) {
      if (newView !== oldView) {
        this.selectedVehicles = [];
      }
    },
  },
  computed: {
    notificationStore() {
      return useNotificationStore();
    },

    tableConfigStore() {
      return useTableConfigStore();
    },

    currentRouteQuery() {
      return this.$route.query;
    },

    searchFields() {
      return rawSearchFields;
    },
  },
  methods: {
    async loadTableConfig() {
      try {
        const columns =
          await this.tableConfigStore.initTableConfiguration(
            "Vehicle",
            "vehicles"
          );

        this.tableColumns = columns.filter(
          (col) => col.enabled !== false
        );
      } catch (error) {
        console.error(
          "Не удалось загрузить конфигурацию таблицы транспорта",
          error
        );
        this.tableColumns = [];
      }
    },

    handleConfigSaved() {
      this.loadTableConfig();
    },

    async getVehiclesRequest() {
      const page = getCurrentPageFromUrl(this.$route);

      try {
        this.dataIsLoading = true;

        const params = {
          page: page,
          ...filtersToApiParams(this.appliedFilters),
        };

        const response = await vehiclesApi.index(
          this.companyId,
          params
        );

        this.vehicles =
          this.tableConfigStore.enrichRecordsWithCustomFields(
            response.data.result,
            this.tableColumns
          );

        const meta = response.data.pagination_meta;
        this.pagination = {
          page: meta.page,
          pages: meta.pages,
          series: meta.series,
          next: meta.next,
          prev: meta.prev,
          count: meta.count,
          limit: meta.limit,
          from: meta.from,
          to: meta.to,
        };

        this.selectedVehicles = [];
      } finally {
        this.dataIsLoading = false;
      }
    },

    updateFiltersFromUrl() {
      this.appliedFilters = getInitialFilters(
        this.$route,
        this.searchFields,
        {
          searchValues: {},
          showInactive: false,
        }
      );
    },

    handleFilterChange(filters) {
      this.appliedFilters = { ...filters };

      const hasValues = Object.values(filters.searchValues).some(
        (v) => v !== null && v !== undefined && v !== ""
      );

      if (hasValues || filters.showInactive) {
        updateUrlWithFilters(this.$router, filters, 1);
      } else {
        clearUrlFilters(this.$router);
      }

      this.selectedVehicles = [];
    },

    goToVehicleCreate() {
      this.$router.push({ name: "VehicleCreate" });
    },

    goToVehicleDocuments() {
      this.$router.push({ name: "VehicleDocuments" });
    },

    goToVehicleDetails(vehicle) {
      const vehicleId = vehicle.id || vehicle;
      this.$router.push({
        name: "VehicleDetails",
        params: { id: vehicleId },
      });
    },

    onSelectionChange(selected) {
      this.selectedVehicles = selected;
    },

    async enableSelected() {
      if (this.selectedVehicles.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы одно транспортное средство"
        );
        return;
      }

      try {
        const enablePromises = this.selectedVehicles.map((vehicle) =>
          vehiclesApi.enable(this.companyId, vehicle.id)
        );

        await Promise.all(enablePromises);

        this.notificationStore.showSuccess(
          `Успешно включено ${this.selectedVehicles.length} транспортных средств`
        );

        await this.getVehiclesRequest();
      } catch (error) {
        console.error(
          "Ошибка при включении транспортных средств:",
          error
        );
      }
    },

    async disableSelected() {
      if (this.selectedVehicles.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы одно транспортное средство"
        );
        return;
      }

      try {
        const disablePromises = this.selectedVehicles.map((vehicle) =>
          vehiclesApi.disable(this.companyId, vehicle.id)
        );

        await Promise.all(disablePromises);

        this.notificationStore.showSuccess(
          `Успешно отключено ${this.selectedVehicles.length} транспортных средств`
        );

        await this.getVehiclesRequest();
      } catch (error) {
        console.error(
          "Ошибка при отключении транспортных средств:",
          error
        );
      }
    },

    async exportToExcel() {
      try {
        const search_params = filtersToApiParams(this.appliedFilters);

        const response = await vehiclesApi.export(
          this.companyId,
          search_params
        );

        exportToExcel(response, "vehicles.xlsx");
      } catch (error) {
        console.error("Ошибка при экспорте в Excel:", error);
      }
    },

    openRouteInNewTab,
  },
  async created() {
    this.updateFiltersFromUrl();
    await this.loadTableConfig();
  },

  async mounted() {
    const storedFilters =
      await this.$refs.filterBlock?.loadStoredFilter();

    if (storedFilters) {
      this.appliedFilters = storedFilters;
      updateUrlWithFilters(this.$router, storedFilters, 1);
    }

    await this.getVehiclesRequest();
  },
};
</script>

<style scoped>
.vehicle-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
}

.vehicle-item {
  cursor: pointer;
}

.vehicle-item:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.item-center {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.item-integration-box {
  margin-top: var(--base-in-indent);
}

.integration-title {
  color: #666;
}

.item-bottom {
  display: flex;
  flex-direction: column;
  margin-top: var(--base-in-indent);
}

</style>
